import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { DatePipe } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ApiError } from '../../../core/auth/auth.models';
import {
  CLOSING_TYPE_LABELS, OUTCOME_LABELS, SITUATION_LABELS, TRIAGE_RESULT_LABELS,
} from '../../../core/occurrence/occurrence.labels';
import {
  ClosingType, OccurrenceOutcome, OccurrenceSituation, OccurrenceState, TriageResult,
} from '../../../core/occurrence/occurrence.models';

interface PublicMessage {
  readonly fromUs: boolean;
  readonly body: string;
  readonly sentAt: string;
}

interface PublicStatus {
  readonly situation: OccurrenceSituation;
  readonly state: OccurrenceState;
  readonly closingType: ClosingType | null;
  /** Só quando fechada: o que ficou apurado. */
  readonly closingResult: string | null;
  /** Triagem: mostra que o caso foi visto e com que decisão (documento de 2026-09-26). */
  readonly triageApplies: boolean;
  readonly triageResult: TriageResult | null;
  readonly triageDecidedAt: string | null;
  /** Explica, mais tarde, porque é que o caso esteve parado. */
  readonly awaitingElements: boolean | null;
  readonly elementsRequestedAt: string | null;
  /** Só quando fechada: em que é que o caso deu. */
  readonly outcome: OccurrenceOutcome | null;
  readonly createdAt: string;
  readonly stateChangedAt: string | null;
  readonly closingDate: string | null;
  readonly messages: readonly PublicMessage[];
}

/**
 * Acompanhamento pelo identificador — o estado, os marcos e a resolução, sem conta e sem revelar
 * quem participou. Os campos são sempre os mesmos: o seguimento da equipa não entra, e o que se
 * quer dizer a quem participou escreve-se na conversa do portal.
 *
 * <p>É também por aqui que a equipa pede elementos adicionais: as mensagens ficam à espera
 * nesta página e a resposta vai com o identificador, nunca com o nome de quem responde.
 */
@Component({
  selector: 'app-occurrence-public-tracking',
  imports: [FormsModule, DatePipe, RouterLink],
  templateUrl: './occurrence-public-tracking.html',
})
export class OccurrencePublicTracking implements OnInit {
  private readonly http = inject(HttpClient);
  private readonly route = inject(ActivatedRoute);

  protected readonly tenant = this.route.snapshot.paramMap.get('tenant') ?? '';
  protected readonly situationLabels = SITUATION_LABELS;
  protected readonly closingTypeLabels = CLOSING_TYPE_LABELS;
  protected readonly triageResultLabels = TRIAGE_RESULT_LABELS;
  protected readonly outcomeLabels = OUTCOME_LABELS;

  protected readonly fromChannel = this.route.snapshot.queryParamMap.get('origem') === 'canal';
  protected code = '';
  protected readonly loading = signal(false);
  protected readonly error = signal<string | null>(null);
  protected readonly status = signal<PublicStatus | null>(null);

  /** Identificador da consulta que correu bem — é com ele que se responde. */
  private confirmedCode = '';
  protected reply = '';
  /** Ficheiro a juntar à resposta — a equipa pede elementos e recebe-os por aqui. */
  protected attachment: File | null = null;
  protected readonly sending = signal(false);
  protected readonly sent = signal(false);
  protected readonly replyError = signal<string | null>(null);

  ngOnInit(): void {
    const fromQuery = this.route.snapshot.queryParamMap.get('codigo');
    if (fromQuery) {
      this.code = fromQuery;
      this.lookup();
    }
  }

  protected lookup(): void {
    const code = this.code.trim().toUpperCase();
    if (!code) {
      // Carregar em Consultar sem escrever nada não dizia nada: parecia que o botão não fazia nada.
      this.error.set('Indique o código de acompanhamento que recebeu ao registar.');
      return;
    }
    this.loading.set(true);
    this.error.set(null);
    this.status.set(null);
    this.http.get<PublicStatus>(this.url(code)).subscribe({
      next: (status) => {
        this.status.set(status);
        this.confirmedCode = code;
        this.loading.set(false);
      },
      error: () => {
        this.error.set('Código não encontrado. Verifique se o escreveu tal como lhe foi entregue (ex.: A2B3-C4D5).');
        this.loading.set(false);
      },
    });
  }

  protected pickFile(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.attachment = input.files?.[0] ?? null;
  }

  protected send(): void {
    if (!this.reply.trim() || !this.confirmedCode) return;
    this.sending.set(true);
    this.replyError.set(null);
    this.http.post<{ messageId: number }>(
      `${this.url(this.confirmedCode)}/mensagens`, { body: this.reply.trim() },
    ).subscribe({
      next: (created) => {
        // O ficheiro segue depois da mensagem, porque é a ela que se agarra.
        const file = this.attachment;
        if (!file) {
          this.finishSend();
          return;
        }
        const form = new FormData();
        form.append('file', file);
        this.http.post(
          `${this.url(this.confirmedCode)}/mensagens/${created.messageId}/anexos`, form,
        ).subscribe({
          next: () => this.finishSend(),
          error: (response: HttpErrorResponse) => {
            // A mensagem já entrou; só o anexo falhou, e é isso que se diz.
            this.finishSend();
            this.replyError.set((response.error as ApiError)?.message
              ?? 'A mensagem foi enviada, mas o ficheiro não. Tente anexá-lo noutra mensagem.');
          },
        });
      },
      error: (response: HttpErrorResponse) => {
        this.sending.set(false);
        this.replyError.set((response.error as ApiError)?.message ?? 'Não foi possível enviar a mensagem.');
      },
    });
  }

  private finishSend(): void {
    this.reply = '';
    this.attachment = null;
    this.sending.set(false);
    this.sent.set(true);
    this.lookup();
  }

  private url(code: string): string {
    return `/api/t/${this.tenant}/publico/ocorrencias/${encodeURIComponent(code)}`;
  }

  protected closed(): boolean {
    return this.status()?.situation === 'FECHADO';
  }
}
